// src/pages/Home.jsx
import { useState } from "react";
import { useAuth } from "../context/AuthContext";

export default function Home() {
  const { authFetch } = useAuth();

  // --- State for “generate” form ---
  const [file, setFile] = useState(null);
  const [processId, setProcessId] = useState(null);
  const [genError, setGenError] = useState("");

  // --- State for “status” form ---
  const [statusId, setStatusId] = useState("");
  const [statusResult, setStatusResult] = useState(null);
  const [reason, setReason] = useState(""); //for fail of status == failed
  const [stacktrace, setStacktrace] = useState(""); //for fail of status == failed
  const [statusError, setStatusError] = useState("");

  // --- State for “delete” action ---
  const [deletedCount, setDeletedCount] = useState(null);
  const [deleteError, setDeleteError] = useState("");

  // Handle the file-upload → POST /api/generate
  const handleGenerateSubmit = async (e) => {
    e.preventDefault();
    setGenError("");
    setProcessId(null);

    if (!file) {
      setGenError("Por favor selecciona un archivo.");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await authFetch("/api/generate", {
        method: "POST",
        body: formData,
      });

      if (!res.ok) throw new Error(`Error ${res.status}`);

      const { task_id } = await res.json();
      setProcessId(task_id);
    } catch (err) {
      setGenError("Error al enviar archivo.");
      console.error(err);
    }
  };

  // Handle the status-check → GET /api/status/:id
  const handleStatusSubmit = async (e) => {
    e.preventDefault();
    setStatusError("");
    setStatusResult(null);

    if (!statusId) {
      setStatusError("Ingresa un ID de proceso.");
      return;
    }

    try {
      const res = await authFetch(`/api/status/${statusId}`);
      if (res.status === 404) {
        setStatusResult("not_found");
        return;
      }
      if (!res.ok) throw new Error(`Error ${res.status}`);

      const { status, reason, stacktrace } = await res.json();
      setStatusResult(status);
      setReason(reason);
      setStacktrace(stacktrace);
    } catch (err) {
      setStatusError("Error al consultar estado.");
      console.error(err);
    }
  };

  const statusToText = {
    "not_found": "Finalizado",
    "active": "En proceso",
    "scheduled": "Programado",
    "failed": "Fallido",
  }

  // Handle delete all .xlsx → DELETE /uploads/xlsx
  const handleDelete = async () => {
    setDeleteError("");
    setDeletedCount(null);

    try {
      const res = await authFetch("/api/uploads/xlsx", { method: "DELETE" });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const { deleted } = await res.json();
      setDeletedCount(deleted);
    } catch (err) {
      console.error(err);
      setDeleteError("Error al eliminar archivos.");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4 space-y-8">
      {/* --- Upload Form --- */}
      <form
        onSubmit={handleGenerateSubmit}
        className="bg-white p-6 rounded-lg shadow-md w-full max-w-sm flex flex-col space-y-4"
      >
        <h2 className="text-lg font-semibold text-center">
          Cargar Excel
        </h2>

        <input
          type="file"
          accept=".xlsx, .xls"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="border rounded px-3 py-2"
        />

        {genError && (
          <p className="text-red-500 text-sm">{genError}</p>
        )}

        <button
          type="submit"
          className="bg-blue-600 text-white py-2 rounded hover:bg-blue-700 transition"
        >
          Enviar
        </button>

        {processId && (
          <div className="mt-2 p-2 bg-green-100 rounded text-center">
            <strong>IdProceso:</strong> {processId}
          </div>
        )}
      </form>

      {/* --- Status Check Form --- */}
      <form
        onSubmit={handleStatusSubmit}
        className="bg-white p-6 rounded-lg shadow-md w-full max-w-sm flex flex-col space-y-4"
      >
        <h2 className="text-lg font-semibold text-center">
          Consultar Estado
        </h2>

        <input
          type="number"
          placeholder="ID de Proceso"
          value={statusId}
          onChange={(e) => setStatusId(e.target.value)}
          className="border rounded px-3 py-2"
        />

        {statusError && (
          <p className="text-red-500 text-sm">{statusError}</p>
        )}

        <button
          type="submit"
          className="bg-green-600 text-white py-2 rounded hover:bg-green-700 transition"
        >
          Consultar
        </button>

        {statusResult && (
          <div className="mt-2 p-2 bg-yellow-100 rounded text-center">
            <strong>Status:</strong> {statusToText[statusResult]}
            {statusResult === "failed" && (
              <div className="mt-2 p-2 bg-red-100 rounded text-left whitespace-pre-wrap break-words max-h-96 overflow-auto">
                <strong>Razón:</strong> {reason}
                <br/><br/>
                <strong>Seguimiento de la pila:</strong> {stacktrace}
              </div>
            )}
          </div>
        )}
      </form>

      {/* --- Delete XLSX Form --- */}
      <div className="bg-white p-6 rounded-lg shadow-md w-full max-w-sm flex flex-col space-y-4">
        <h2 className="text-lg font-semibold text-center">
            Eliminar XLSX en servidor
            <span
                className="ml-2 text-gray-500 cursor-help"
                title="Solamente se borrará del servidor, no de OneDrive."
                >
                ℹ️
            </span>
        </h2>

        <button
          onClick={handleDelete}
          className="bg-red-600 text-white py-2 rounded hover:bg-red-700 transition"
        >
          Eliminar archivos
        </button>

        {deleteError && (
          <p className="text-red-500 text-sm">{deleteError}</p>
        )}

        {deletedCount !== null && (
          <div className="mt-2 p-2 bg-green-100 rounded text-center">
            <strong>Archivos eliminados:</strong> {deletedCount}
          </div>
        )}
      </div>
    </div>
  );
}
